import {
  getColumnWidth as superTableColumnWidth,
  getDateOperators
} from '@nile/components';
import {
  getDefaultValue,
  getVariables,
  checkDynamicQuery,
  getDynamicQuery,
  generateOptions,
  fetchValues,
  getAsyncOptions
} from './all-monitored-assets-view-filter-helper';
import { gql } from '@apollo/client';
import {
  columnsConfig,
  getAttributeName,
  getColumnWidth,
  getColumnAccessor,
  getColumnCellType
} from '@nile/rmm/src/modules/asset-management/services/asset-list-helper';

import {
  ALL_ASSET_TYPES,
  getAssetsEntityType,
  SUPPORTED_MODULE_TYPES
} from '@nile/rmm/src/constants';

const isSortable = (attributes, col) => {
  if (attributes?.includes(col.name)) {
    return true;
  }
  return false;
};

const isFilterable = (attributes, col, restrictFilter) => {
  if (restrictFilter) {
    return false;
  }
  if (attributes?.includes(col.name)) {
    return true;
  }
  return false;
};

const performActions = (col: { name: string }, callBack: Function) => {
  switch (col.name) {
    case 'name':
      return {
        onClick: (data) => {
          callBack('detailsView', data);
        }
      };
    case 'ACTION':
      return {
        onEdit: () => {
          callBack('refreshListData');
        },
        onDelete: () => {
          callBack('refreshListData');
        }
      };
    case 'assetActivate':
      return {
        onComplete: () => {
          callBack('refreshListData');
        }
      };
    default:
      break;
  }
};

/**
 *
 * @param cols - list of columns
 * @param columnWidthObject - column size personalization
 * @param searchAttributes - Filter condition column
 * @param sortAttributes - Sort condition column
 * @param columnAction - Bulk/inline action on success callback function
 * @param updateAllConditions - Inline table condition update
 * @returns
 */
const constructTableData = (
  cols: any[],
  columnWidthObject: any,
  searchAttributes: any,
  sortAttributes: any,
  columnAction: (args: string) => void,
  updateAllConditions: (condition: any, selectedField: any) => void,
  module: string,
  viewSubType: string,
  activeView: any,
  isAssetInClientPortal: boolean,
  restrictFilter: boolean,
  isAssetType: boolean = true
) => {
  const columns = [];
  const allColumns = [
    ...(isAssetType
      ? [
          {
            label: '',
            name: 'id',
            key: ' ',
            identifier: ' ',
            type: 'TYPE',
            filterType: 'text',
            dataType: 'string',
            size: 50
          }
        ]
      : []),
    ...cols
  ];
  allColumns.map((col, idx) => {
    columns.push({
      header: col.label,
      accessorKey: getColumnAccessor(col),
      minSize: getColumnWidth(col.name, true),
      size:
        col.name === 'id'
          ? 30
          : superTableColumnWidth(columnWidthObject, col.name),
      Header: col.label,
      minWidth: getColumnWidth(col.name, true),
      width: getColumnWidth(col.name),
      accessor: getColumnAccessor(col),
      type: getColumnCellType(col, activeView, isAssetInClientPortal),
      id: col.name,
      disableResizing: false,
      className: 'sticky',
      disableSortBy: !isSortable(sortAttributes, col),
      editable: false,
      disableFilters: !isFilterable(searchAttributes, col, restrictFilter),
      field: col,
      actions: performActions(col, columnAction),
      module: module,
      meta: {
        module,
        viewSubType
      },
      helpers: {
        getDefaultValue,
        checkDynamicQuery,
        updateConditions: updateAllConditions,
        getDateOperators,
        getVariables: getVariables.bind(this, col),
        getDynamicQuery,
        generateOptions,
        fetchValues,
        getAsyncOptions,
        entity: 'Asset'
      }
    });
  });

  return {
    columnsGroup: [{ Header: 'Name', header: 'Name', columns }]
  };
};

const defaultListInfoOptions = {
  page: 1,
  condition: null,
  sort: [
    {
      attribute: 'name',
      order: 'ASC'
    }
  ]
};

const getNetworkEntityVariables = (type: string, entityName?: string) => {
  const defaultEntityName = [
    'Client',
    'Site',
    'AssetClass',
    'AssetMake',
    'AssetModel',
    'Requester',
    'policyCategory',
    'BusinessFunction'
  ];

  defaultEntityName.push(entityName || getAssetsEntityType(type) || 'asset');

  return defaultEntityName;
};

const getEntityVariables = (type: string, entityName?: string) => {
  if (type === ALL_ASSET_TYPES.ALL_MONITORED_ASSET) {
    return [
      'Client',
      'Site',
      'AssetClass',
      'IPAsset',
      'Requester',
      'policyCategory',
      'BusinessFunction'
    ];
  }

  if (type === ALL_ASSET_TYPES.WORKSTATION_ASSET) {
    return [
      'Client',
      'Site',
      'AssetClass',
      'asset',
      'Requester',
      'policyCategory',
      'BusinessFunction',
      'WorkStation'
    ];
  }

  return getNetworkEntityVariables(type, entityName);
};

const getMonitoredAssetsQuery = (
  supportedModules?: string[],
  tableCols: any
) => {
  let customFields = [];
  const isWorkStation = supportedModules?.includes(
    SUPPORTED_MODULE_TYPES.ENDPOINT_MANAGEMENT
  );

  if (tableCols.length > 0) {
    const fields = tableCols[0].columns.filter((column) => {
      return column.field.isCustom === true;
    });

    if (fields.length > 0) {
      customFields = fields.map((cf) => cf.field.name);
    }
  }
  const fields = isWorkStation
    ? `serialNumber
    manufacturer
    model
    hostName
    primaryIp
    platform
    domain
    status
    tag
    sysUptime
    teamViewerIntegrated
    splashtopIntegrated
    cwcIntegrated
    primaryMac
    lastReportedTime
    patchStatus
    bitLockerStatus
    warrantyExpiryDate
    purchasedDate
    platformFamily
    platformVersion
    platformCategory
    agentVersion
    assetAge
    rebootNeeded
    rebootCause
    registeredTime
    serialKey
    biosVersion
  `
    : `status
      ipAddress
      macAddress
      publicIpAddress
      serialNumber
      hostName
      sysUptime
      assetMake {
        makeId
        name
      }
      assetModel {
        modelId
        name
      }
      policyCategory {
        name
      }
      department {
        name
        businessFunctionId
      }
      `;

  return gql`
    query($listInfo: ListInfoInput!, $viewType: String, $assetClassId: ID) {
      getMonitoredAssets(
        listInfo: $listInfo
        viewType: $viewType
        assetClassId: $assetClassId
      ) {
        assets {
          assetId
          name
          loggedInUser
          assetClass {
            classId
            name
            iconClass
          }
          client {
            accountId
            name
          }
          site {
            id
            name
          }
          owner {
            userId
            name
          }
          ${
            customFields.length > 0
              ? `customFields( fields: ["${customFields.join('","')}"])`
              : ''
          }
          ... on ${isWorkStation ? 'WorkStation' : 'IPAsset'}  {
            __typename
            ${fields}
          }
        }
        listInfo {
          page
          pageSize
          condition
          totalCount
          sort {
            attribute
            order
          }
          hasMore
        }
      }
    }
  `;
};

export {
  columnsConfig,
  getAttributeName,
  getColumnWidth,
  getColumnAccessor,
  getColumnCellType,
  constructTableData,
  performActions,
  defaultListInfoOptions,
  getEntityVariables,
  getMonitoredAssetsQuery,
  getNetworkEntityVariables
};
